
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../i18n';

const Navbar = () => {
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Track window width for responsive rendering
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768 && menuOpen) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);

  const handleAccountClick = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/mon-compte');
    } else {
      navigate('/login');
    }
  };

  return (
    <nav style={styles.navbar}>
      {/* Left: Logo (always visible) */}
      <div style={styles.leftCol}>
        <Link to="/">
          <img src="/assets/site/wlogo2.svg" alt="Logo" style={styles.logo} />
        </Link>
      </div>

      {/* Desktop Center Menu */}
      <div style={{ ...styles.centerCol, display: windowWidth > 768 ? 'flex' : 'none' }}>
        <div style={styles.centerMenuContainer} className="navbar-center-menu">
          <Link
            to="/projets"
            style={{
              ...styles.menuItem,
              fontWeight: hoveredItem === t('exploreMenu') ? 'bold' : '200',
            }}
            onMouseEnter={() => setHoveredItem(t('exploreMenu'))}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {t('exploreMenu') || 'Idées DIY'}
          </Link>
          <Link
            to="/categories"
            style={{
              ...styles.menuItem,
              fontWeight: hoveredItem === 'Catégories' ? 'bold' : '200',
            }}
            onMouseEnter={() => setHoveredItem('Catégories')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            Catégories
          </Link>
          <Link
            to="/objets"
            style={{
              ...styles.menuItem,
              fontWeight: hoveredItem === t('categoriesMenu') ? 'bold' : '200',
            }}
            onMouseEnter={() => setHoveredItem(t('categoriesMenu'))}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {t('categoriesMenu') || 'Objets'}
          </Link>
        </div>
      </div>

      {/* Desktop Right: Account icon */}
      <div style={{ ...styles.rightCol, display: windowWidth > 768 ? 'flex' : 'none' }}>
        <span
          onClick={handleAccountClick}
          style={styles.accountIconRight}
          className="navbar-account-right"
        >
          <img src="/assets/site/account.svg" alt={t('accountAlt') || 'Mon compte'} style={styles.accountIcon} />
        </span>
      </div>

      {/* Mobile Burger Icon */}
      <div style={{ display: windowWidth <= 768 ? 'flex' : 'none', marginLeft: 'auto', zIndex: 20 }}>
        <button
          aria-label="Ouvrir le menu"
          style={styles.burgerButton}
          onClick={() => setMenuOpen(true)}
        >
          <span style={styles.burgerBar}></span>
          <span style={styles.burgerBar}></span>
          <span style={styles.burgerBar}></span>
        </button>
      </div>

      {/* Mobile Overlay Menu */}
      {menuOpen && (
        <div style={styles.mobileOverlay}>
          <button
            aria-label="Fermer le menu"
            style={styles.closeButton}
            onClick={() => setMenuOpen(false)}
          >
            &times;
          </button>
          <div style={styles.mobileMenuLinks}>
            <Link to="/projets" style={styles.mobileMenuItem} onClick={() => setMenuOpen(false)}>
              {t('exploreMenu') || 'Idées DIY'}
            </Link>
            <Link to="/categories" style={styles.mobileMenuItem} onClick={() => setMenuOpen(false)}>
              Catégories
            </Link>
            <Link to="/objets" style={styles.mobileMenuItem} onClick={() => setMenuOpen(false)}>
              {t('categoriesMenu') || 'Objets'}
            </Link>
            <span
              onClick={() => { setMenuOpen(false); handleAccountClick(); }}
              style={styles.mobileAccountIcon}
              className="navbar-account-mobile"
            >
              <img src="/assets/site/account.svg" alt={t('accountAlt') || 'Mon compte'} style={styles.accountIcon} />
            </span>
          </div>
        </div>
      )}
    </nav>
  );
};

const styles = {
  navbar: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: 'var(--green-eucalyptus)',
    color: 'var(--white-off)',
    width: '100%',
    minHeight: '60px',
    boxSizing: 'border-box',
  },
  leftCol: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    minWidth: '80px',
  },
  centerCol: {
    flex: '1 1 0%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: 0,
    maxWidth: 'none',
  },
  rightCol: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '80px',
  },
  burgerButton: {
    background: 'none',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 44,
    height: 44,
    cursor: 'pointer',
    padding: 0,
    marginRight: 10,
    zIndex: 21,
  },
  burgerBar: {
    width: 28,
    height: 4,
    background: 'var(--white-off)',
    margin: '3px 0',
    borderRadius: 2,
    transition: 'all 0.2s',
    display: 'block',
  },
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(30, 60, 40, 0.98)',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    padding: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: 'var(--white-off)',
    fontSize: 44,
    fontWeight: 400,
    cursor: 'pointer',
    margin: '18px 24px 0 0',
    alignSelf: 'flex-end',
    zIndex: 101,
  },
  mobileMenuLinks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: 40,
    gap: 32,
  },
  mobileMenuItem: {
    color: 'var(--white-off)',
    fontSize: 32,
    textDecoration: 'none',
    fontWeight: 600,
    padding: '12px 0',
    width: '100%',
    textAlign: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontFamily: `'Montserrat', sans-serif`,
  },
  mobileAccountIcon: {
    marginTop: 18,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)',
  },
  logo: {
    height: '60px',
    filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
    marginLeft: '10px',
  },
  centerMenuContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '30px',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  menuItem: {
    textDecoration: 'none',
    color: 'var(--white-off)',
    fontFamily: `'Montserrat', sans-serif`,
    fontSize: '26px',
    cursor: 'pointer',
    marginTop: '5px',
    transition: 'font-weight 0.2s ease-in-out',
    whiteSpace: 'nowrap',
  },
  accountIconRight: {
    marginLeft: '10px',
    marginRight: '10px',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    background: 'none',
    border: 'none',
    display: 'flex',
  },
  accountIcon: {
    width: '40px',
    height: '40px',
    filter: 'invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
    marginLeft: 0,
  },
};

export default Navbar;